/**
 * Platform analytics charts for super admin monitoring.
 */
import { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import Sidebar_Progress from '../../layouts/Admin_Sidebar_Progress.component';
import LoginHeaderOperational from '../LoginHeaderOperational';
import { API_URL } from '../../../constant/API_Settings';
import { fetchPlatformSuperAdminProfile } from '../../../utils/platformAuth';
import { fetchAdminCharts } from './adminApi';
import './admin-dashboard.css';

const COLORS = ['#198754', '#dc3545', '#0d6efd', '#fd7e14', '#6f42c1'];

const AdminPlatformAnalytics = (props) => {
  const history = useHistory();
  const [charts, setCharts] = useState(null);

  useEffect(() => {
    fetchPlatformSuperAdminProfile(API_URL, localStorage.getItem('AUTH_TOKEN'))
      .then((profile) => {
        if (profile) {
          fetchAdminCharts().then(setCharts).catch(console.error);
        } else {
          history.goBack();
        }
      })
      .catch(() => history.goBack());
  }, [history]);

  if (!charts) {
    return <div className="text-center py-5">Loading analytics...</div>;
  }

  return (
    <div className="main-container page-wrap admin-platform">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={27} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Platform Analytics" />
          <div className="container pb-3 mt-3">
            <Link to="/admin_dashboard" className="small">← Control Center</Link>

            <div className="admin-panel mt-3">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div>
                  <h5 className="mb-1">Most active pumps</h5>
                  <small className="text-muted">Top 5 by login/activity in the last 14 days</small>
                </div>
                <Link to="/admin-pumps-details" className="btn btn-sm btn-outline-primary">View all pumps</Link>
              </div>

              {(charts.most_active_pumps || []).length === 0 ? (
                <p className="text-muted mb-0">No pump activity recorded yet.</p>
              ) : (
                <>
                  <div className="table-responsive mb-3">
                    <table className="table table-sm table-hover mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Pump</th>
                          <th>Active days</th>
                          <th>Last seen</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(charts.most_active_pumps || []).map((pump, idx) => (
                          <tr key={pump.pump_id}>
                            <td>{idx + 1}</td>
                            <td>
                              <strong>{pump.pump_name}</strong>
                              {pump.pump_status !== 1 && (
                                <span className="admin-badge inactive ms-2">Inactive</span>
                              )}
                            </td>
                            <td>{pump.active_days}</td>
                            <td>{pump.last_seen || '—'}</td>
                            <td>
                              <Link to={`/admin-pump/${pump.pump_id}`} className="btn btn-sm btn-primary">
                                Open
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={charts.most_active_pumps || []} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="pump_name" width={140} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="active_days" fill="#198754" name="Active days (14d)" />
                    </BarChart>
                  </ResponsiveContainer>
                </>
              )}
            </div>

            <div className="row mt-3">
              <div className="col-lg-6">
                <div className="admin-panel">
                  <h5>Subscription revenue (6 months)</h5>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={charts.subscription_revenue || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`} />
                      <Bar dataKey="amount" fill="#0d6efd" name="Revenue" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="admin-panel">
                  <h5>Pump status</h5>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={charts.pump_status || []} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                        {(charts.pump_status || []).map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="admin-panel">
                  <h5>Daily active pumps (14 days)</h5>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={charts.daily_activity || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="active_pumps" stroke="#198754" name="Active pumps" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="admin-panel">
                  <h5>Open alerts by type</h5>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={charts.alerts_by_type || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="type" width={120} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#fd7e14" name="Alerts" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPlatformAnalytics;
